<template>
  <!--
    SimpleCellElementLite, для DisplayType 0 (Simple) и 6 (SimpleAsTag).
    Обычный путь простого значения (renderedContent).
    Тяжёлые простые ячейки, то есть имя тега, артикул и комментарий склада, отбираются
    в QTLiteRowCells предикатом isHeavySimple и уходят в свои компоненты
    TagNameCellElementLite, VendorCodeCellElementLite и NotesCellElementLite,
    сюда они не попадают.

    Multi-root template: один <span> для DisplayType 6 (grey), либо <span> с v-html для остальных.
  -->
  <span
    v-if="element.DisplayType === 6"
    class="qtl-simple-tag"
    v-html="renderedHtml"
    @click="onCellClick"
  />
  <span
    v-else
    class="qtl-simple-html"
    v-html="renderedHtml"
    @click="onCellClick"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { i18n } from "@/i18n";
import type { TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import { AnalyticsV2ElementType, ColumnCategory } from "@/models/etc/types";
import { generalStore } from "@/store/general";
import { auxStore } from "@/store/auxillary";
import { formatSimpleHtml, wrapPhones } from "./cellFormat";

const props = defineProps<{
  element: TableDataCell;
  /** Оставлено для совместимости; в lite сейчас не используется. */
  entityId?: number;
  creatorId?: number;
}>();

defineEmits<{
  (e: "refreshNeeded"): void;
}>();

const t = (k: string) => i18n.global.t(k);
const gstore = generalStore();
const aux = auxStore();

/* Контактная колонка (детали персоны у элемента-персоны): в ней номера телефонов
   делаем кликабельными для отправки запроса на звонок (без нативного tel:-набора). */
const isContactType = computed<boolean>(() =>
  props.element.Category === ColumnCategory.PersonDetails &&
  gstore.selectedItem?.DestType === AnalyticsV2ElementType.Person,
);

/** v-once невозможен (контент меняется при reactivity row-replace), но работа функции дешёвая. */
const renderedHtml = computed(() => wrapPhones(formatSimpleHtml(props.element, t), isContactType.value));

// Анти-дребезг: защита от двойного запроса на звонок при быстром двойном клике,
// не зовём callPhoneNumber чаще раза в 600мс на одну ячейку.
let lastCallTs = 0;

function onCellClick(e: MouseEvent) {
  const target = e.target as HTMLElement | null;
  const phone = target?.closest?.(".phone-number") as HTMLElement | null;
  if (!phone) return;
  const number = phone.dataset.phone;
  if (!number) return;
  const now = Date.now();
  if (now - lastCallTs < 600) return;
  lastCallTs = now;
  aux?.callPhoneNumber?.(number);
}
</script>

<style lang="scss" scoped>

.qtl-simple-html {
  word-break: normal;
  overflow-wrap: anywhere;
  font-size: 13px;
}

.qtl-simple-tag {
  color: grey;
  word-break: normal;
  overflow-wrap: anywhere;
  font-size: 13px;
}

.qtl-simple-html :deep(.phone-number),
.qtl-simple-tag :deep(.phone-number) {
  color: #438451;
  text-decoration: none;
  cursor: pointer;
  white-space: normal;
}

.qtl-simple-html :deep(.phone-number:hover),
.qtl-simple-tag :deep(.phone-number:hover) {
  text-decoration: underline;
}
</style>
